import { withFocusRing } from "@components/Accessibility/FocusRing/FocusRing";
import { ComponentProps } from "@ts/components";
import { clsx } from "clsx";
import React, { ChangeEvent, MouseEvent } from "react";

type FormRadioProps = Omit<ComponentProps, "name"> & {
  className?: string;
  dataTestId?: string;
  handleChange?: (
    event: MouseEvent<HTMLDivElement> | ChangeEvent<HTMLInputElement>,
  ) => void;
  internalState?: boolean;
  label?: string;
  name: string;
  value: boolean;
  tabIndex: number;
};

const Wrapper = (props: any) => (
  <div
    {...props}
    className={clsx(
      "relative flex cursor-pointer flex-row items-baseline",
      props.className,
    )}
  />
);
const Label = (props: any) => (
  <span
    {...props}
    className={clsx(
      "ml-maxi inline-block self-center text-hint leading-h5 md:ml-maxi md:text-body",
      props.className,
    )}
  />
);
const Input = withFocusRing((props: any) => (
  <input
    {...props}
    className={clsx(
      props.className,
      "inset-0 absolute block h-full w-full cursor-pointer appearance-none",
    )}
  />
));
const StyledIconWrapper = ({ children }: { children: React.ReactNode }) => (
  <div className="relative mt-pico h-midi">{children}</div>
);
const StyledIconInner = (props: any) => (
  <div
    {...props}
    className={clsx(
      props.className,
      "duration-fast transition-layout absolute",
      props.display ? "block" : "hidden",
    )}
  />
);

const FormRadio = ({
  className,
  dataTestId,
  handleChange,
  internalState,
  label,
  name,
  value,
  tabIndex,
}: FormRadioProps) => {
  const htmlTagRegex = /<.+?>/;
  const createHtmlMarkup = (htmlString) => {
    return { __html: htmlString };
  };
  const isIncludingHtmlTag = label?.match(htmlTagRegex);

  return (
    <Wrapper
      onClick={!internalState ? handleChange : undefined}
      className={className}
    >
      <StyledIconWrapper>
        <StyledIconInner display={value}>
          <IconRadioButtonEnabled />
        </StyledIconInner>
        <StyledIconInner display={!value}>
          <IconRadioButton />
        </StyledIconInner>
      </StyledIconWrapper>
      {isIncludingHtmlTag ? (
        <Label dangerouslySetInnerHTML={createHtmlMarkup(label)} />
      ) : (
        <Label>{label}</Label>
      )}
      {internalState && (
        <Input
          data-testid={dataTestId}
          type="checkbox"
          name={name}
          onChange={handleChange}
          value={value?.toString()}
          tabIndex={tabIndex}
        />
      )}
    </Wrapper>
  );
};

function IconRadioButtonEnabled() {
  return (
    <svg viewBox="0 0 18 18" fill="none" className="w-[16px]">
      <circle
        cx="9.31165"
        cy="8.91251"
        r="8"
        stroke="currentColor"
        strokeWidth="1.25"
      />
      <circle cx="9.31165" cy="8.91238" r="5" fill="currentColor" />
    </svg>
  );
}

function IconRadioButton() {
  return (
    <svg viewBox="0 0 18 18" fill="none" className="w-[16px]">
      <circle
        cx="9.31165"
        cy="9"
        r="8"
        stroke="currentColor"
        strokeWidth="1.25"
      />
    </svg>
  );
}

export default FormRadio;
